<script context="module">
  async function _decrypt(sessionId, encrypted) {
    const res = await fetch("/api/decrypt", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId, encrypted })
    });
    if (res.ok) {
      return res.json();
    }
  }
</script>

<script>
  import { onMount } from "svelte";
  import { page } from "$app/stores";

  import { FormComponent, LoadingIndicator } from "@obamafoundation/of-design-system";

  import { extractFormFields, UTILITY_FIELDS } from "$lib/utilities/form";
  import { initWebSocket } from "$lib/utilities/socket";
  import logger from "$lib/utilities/logger";
  import { get } from "svelte/store";
  import pageSpanStore from "$lib/stores/pageSpanStore";
  import { SpanStatusCode } from "@opentelemetry/api";

  const title = "Use your own device";
  let description = "Getting the form ready";
  const confirmation = {
    title: "Thanks!",
    subtitle: "Next step",
    description: `Return to the interactive and press the "I'm finished" button to continue.`
  };

  let stationID;
  let mediaID;
  let sessionID;

  let formId;
  let formFields = [];
  let isParsing = true;

  let isSubmitting = false;
  let isFormSubmitSuccess = false;
  let formSubmitErrorMessage = "";

  /** @type {WebSocket}*/
  let socket;

  onMount(async () => {
    isParsing = true;
    logger.debug("Parsing form fields from query parameters");

    const params = $page.url.searchParams;

    stationID = params.get("station");
    mediaID = params.get("media");
    sessionID = params.get("sessionId");
    const code = params.get("code");

    if (!sessionID || !stationID || !mediaID || !code) {
      logger.error("Missing required query parameters", {
        sessionId: sessionID,
        stationId: stationID,
        mediaId: mediaID,
        code
      });
      formSubmitErrorMessage =
        "Missing required parameters: `sessionId`, `station`, `media`, and `code`.";
      isParsing = false;
      return;
    }

    const decrypted = await _decrypt(sessionID, decodeURIComponent(code));

    if (!decrypted) {
      formSubmitErrorMessage = "Failed to decrypt form fields from query params!";
      logger.error(formSubmitErrorMessage);
      isParsing = false;
      return;
    }

    if (!decrypted?.data) {
      formSubmitErrorMessage = "No form fields found!";
      logger.error(formSubmitErrorMessage);
      isParsing = false;
      return;
    }

    formId = decrypted.data.id;

    if (decrypted.data.children) {
      logger.debug("Extracting a list of fields");
      formFields = extractFormFields(decrypted.data);
    } else {
      logger.debug("Extracting a single field and marking it required");
      formFields = extractFormFields({
        children: [{ ...decrypted.data, required: true }]
      });
    }

    description = "Enter your information below.";
    isParsing = false;
  });

  onMount(() => {
    logger.debug("Initializing WebSocket client");

    socket = initWebSocket(mediaID, stationID);

    return () => {
      socket?.close();
    };
  });

  onMount(() => {
    pageSpanStore.initialize({
      path: get(page).url.pathname,
      viewKey: title,
      station: stationID,
      session_id: sessionID
    });

    return () => {
      pageSpanStore.clear();
    };
  });

  let queue = Promise.resolve();
  function queueMessage(data, ws = socket) {
    queue = queue.then(() => {
      return new Promise((resolve) => {
        ws.send(data);
        setTimeout(() => {
          resolve();
        }, 500);
      });
    });
  }

  async function handleSubmit(evt) {
    pageSpanStore.withChild(
      "handleFormSubmit",
      (span) => {
        isSubmitting = true;

        if (!socket || socket?.readyState !== socket?.OPEN) {
          formSubmitErrorMessage = "WebSocket is not ready! Please refresh the page and try again.";
          isSubmitting = false;
          logger.error(formSubmitErrorMessage);
          return;
        }

        /** @type {Record<string, any>}*/
        const formData = evt.detail || {};
        Object.entries(formData).forEach(([key, value]) => {
          if (UTILITY_FIELDS.includes(key)) {
            return;
          }
          const valueChangeEvent = {
            type: "valueChange",
            timeStamp: new Date(),
            contentID: key,
            value: value,
            media: mediaID,
            station: stationID,
            sessionID
          };
          logger.debug("Sending `valueChange` event over WebSocket:", valueChangeEvent);
          queueMessage(JSON.stringify(valueChangeEvent));
        });

        isSubmitting = false;
        isFormSubmitSuccess = true;

        span.setStatus({ code: SpanStatusCode.OK });
      },
      {
        formId,
        mediaId: mediaID,
        stationId: stationID,
        sessionID,
        fields: JSON.stringify(evt.detail)
      }
    );
    pageSpanStore.clear();
  }
</script>

<svelte:head>
  <title>Use Your Own Device</title>
</svelte:head>

{#if isParsing}
  <div
    class="mx-auto max-w-[1161px] flex flex-row px-4 py-8 min-[390px]:px-10 md:px-10 md:py-[50px] lg:p-[48px] gap-x-1 md:gap-x-2 body-md whitespace-pre-wrap text-black"
  >
    <span>{description}</span>
    <LoadingIndicator />
  </div>
{:else}
  <div>
    <FormComponent
      id={formId}
      layout="stacked"
      theme="lightGrey"
      {title}
      {description}
      {formFields}
      {formSubmitErrorMessage}
      {isSubmitting}
      {isFormSubmitSuccess}
      {confirmation}
      onSubmit={handleSubmit}
    />
  </div>
{/if}
